/**
 * Normalize agent checkout / purchase payloads (`lines` + `totals`).
 */

function parseNum(v, fallback = 0) {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : fallback;
}

function roundMoney(n) {
  return Math.round(parseNum(n) * 100) / 100;
}

/** Default sales tax rate when backend sends total with tax but omits tax fields. */
export const DEFAULT_AGENT_TAX_PERCENT = 5;

/**
 * Row label for the money taken off a bill. Every discount this storefront
 * gives today is a price match the agent made, and the checkout summary says
 * so; the confirmation page and the agent bill rows use the same words, so
 * the shopper never reads "Discount" on one screen and "Price-match
 * adjustment" on the next for the same figure.
 */
export const PRICE_MATCH_ADJUSTMENT_LABEL = "Price-match adjustment";

/**
 * One line item for checkout / order confirmation UI.
 * When `price_matched` is true, show the reduced `lineTotal` (not discount_amount).
 */
export function normalizeAgentLine(row) {
  if (!row || typeof row !== "object") return null;

  const priceMatched = Boolean(row.price_matched ?? row.priceMatched);
  const quantity = Math.max(1, Math.floor(parseNum(row.quantity, 1)));
  const unitPrice = parseNum(row.price, 0);
  const lineTotal =
    row.line_total != null
      ? parseNum(row.line_total, unitPrice * quantity)
      : row.lineTotal != null
        ? parseNum(row.lineTotal, unitPrice * quantity)
        : unitPrice * quantity;
  const originalPrice =
    row.original_price != null
      ? parseNum(row.original_price, null)
      : row.originalUnitPrice != null
        ? parseNum(row.originalUnitPrice, null)
        : null;

  const img =
    (typeof row.image_uri === "string" && row.image_uri) ||
    (typeof row.uri === "string" && row.uri) ||
    (typeof row.image === "string" && row.image) ||
    "";

  return {
    id: row.product_id ?? row.article_id ?? row.id ?? null,
    title: row.title || row.product_title || row.name || "Item",
    quantity,
    size: row.size ?? null,
    currency: row.currency || "USD",
    priceMatched,
    /** Effective unit price after match (for cart-style displays). */
    price: quantity > 0 ? lineTotal / quantity : unitPrice,
    lineTotal,
    /** Original unit price before match — shown struck-through when matched. */
    originalUnitPrice:
      priceMatched && originalPrice != null && originalPrice > 0
        ? originalPrice
        : null,
    originalLineTotal:
      priceMatched && originalPrice != null && originalPrice > 0
        ? originalPrice * quantity
        : null,
    image: img,
    image_uri: img,
  };
}

/**
 * @param {unknown} lines
 * @returns {ReturnType<typeof normalizeAgentLine>[]}
 */
export function normalizeAgentLines(lines) {
  if (!Array.isArray(lines)) return [];
  return lines.map(normalizeAgentLine).filter(Boolean);
}

/**
 * @param {unknown} totals
 */
export function normalizeAgentTotals(totals) {
  if (!totals || typeof totals !== "object") return null;

  const subtotal = parseNum(totals.subtotal ?? totals.original_subtotal, 0);
  const discountTotal = parseNum(totals.discount_total ?? totals.discountTotal, 0);
  const shipping = parseNum(totals.shipping, 0);
  let taxPercent = parseNum(totals.tax_percent ?? totals.taxPercent, 0);
  let tax = parseNum(totals.tax_amount ?? totals.tax ?? totals.taxAmount, 0);
  let total = parseNum(totals.total, 0);

  // Compute tax from percent when amount is missing
  if (tax === 0 && taxPercent > 0 && subtotal > 0) {
    tax = roundMoney(subtotal * taxPercent / 100);
  }

  // Infer tax from total − subtotal (backend often sends total with tax baked in)
  if (tax === 0 && total > 0 && subtotal > 0 && total > subtotal) {
    const implied = roundMoney(total - subtotal + discountTotal - shipping);
    if (implied > 0) {
      tax = implied;
      if (taxPercent === 0) {
        const pct = roundMoney((tax / subtotal) * 100);
        taxPercent = Math.abs(pct - DEFAULT_AGENT_TAX_PERCENT) < 0.2
          ? DEFAULT_AGENT_TAX_PERCENT
          : pct;
      }
    }
  }

  // Default agent rate when tax fields are absent and total is not yet known
  if (
    tax === 0 &&
    taxPercent === 0 &&
    subtotal > 0 &&
    (total === 0 || total > subtotal)
  ) {
    taxPercent = DEFAULT_AGENT_TAX_PERCENT;
    tax = roundMoney(subtotal * taxPercent / 100);
  }

  if (total === 0) {
    total = roundMoney(subtotal - discountTotal + shipping + tax);
  }

  return {
    subtotal,
    originalSubtotal: parseNum(totals.original_subtotal ?? totals.originalSubtotal, subtotal),
    tax,
    taxPercent,
    discountTotal,
    shipping,
    total,
    currency: totals.currency || "USD",
    itemsCount: parseNum(totals.items_count ?? totals.itemsCount, 0),
  };
}

/**
 * Pull billing/lines/totals from a full agent `lastResponse`.
 */
export function extractAgentBillingFromResponse(lastResponse) {
  if (!lastResponse || typeof lastResponse !== "object") {
    return { billing: null, lines: [], totals: null };
  }

  const lr = /** @type {Record<string, unknown>} */ (lastResponse);
  const action =
    lr.action != null && typeof lr.action === "object" && !Array.isArray(lr.action)
      ? /** @type {Record<string, unknown>} */ (lr.action)
      : null;
  const summary =
    lr.summary != null && typeof lr.summary === "object" && !Array.isArray(lr.summary)
      ? /** @type {Record<string, unknown>} */ (lr.summary)
      : null;

  const billing =
    lr.billing ??
    action?.billing_details ??
    null;

  const rawLines =
    lr.lines ??
    summary?.lines ??
    action?.cart_data ??
    [];

  const rawTotals = lr.totals ?? summary?.totals ?? null;

  return {
    billing,
    lines: normalizeAgentLines(rawLines),
    totals: normalizeAgentTotals(rawTotals),
  };
}

/** Build price breakdown rows for UI from normalized totals. */
export function buildBillRowsFromTotals(totals, formatPrice) {
  if (!totals) return [];
  const fmt = formatPrice || ((n) => parseNum(n).toFixed(2));

  const rows = [
    { label: "Subtotal", value: `$${fmt(totals.subtotal)}` },
  ];

  if (totals.discountTotal > 0) {
    rows.push({
      label: PRICE_MATCH_ADJUSTMENT_LABEL,
      value: `-$${fmt(totals.discountTotal)}`,
      green: true,
    });
  }

  const taxLabel =
    totals.taxPercent > 0
      ? `Tax (${totals.taxPercent}%)`
      : `Tax (${DEFAULT_AGENT_TAX_PERCENT}%)`;
  rows.push({
    label: taxLabel,
    value: `$${fmt(totals.tax)}`,
  });

  rows.push({
    label: "Total",
    value: `$${fmt(totals.total)}`,
    bold: true,
  });

  return rows;
}
